import { useRef } from "react";
import { useDispatch, useStore } from "react-redux";

import { streamAnalysisResult } from "@/api/agenticAI/streamAnalysisResult";
import { SPATIAL_ANALYSIS_RESULT_DATASET_NAME } from "@/constants/dataset";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDataset } from "@/hooks/map/useDataset";
import type { RootState } from "@/store";
import { addDataset, addRawDataset } from "@/store/slice/datasetSlice";
import type { SpatialAggregationType } from "@/types/common";
import { convertDatasetResponseToFeatureCollection } from "@/utils/dataset";

/**
 * Puts a finished analysis's result grid on the shared map.
 *
 * The one thing the SDK cannot do for itself, and the only fact the agent trusts
 * this client for: it follows the run, renders the stepper and answers the pause
 * on its own, then asks for this. Resolving is a claim that the user can see the
 * result — `result_loaded: true` is repeated back to them — so this resolves
 * only once the layer's source is on the map, and throws otherwise, which is
 * what earns `RESULT_LOAD_FAILED` and the agent's offer to load it again.
 *
 * Only the result grid is painted. The analysis stream re-delivers the input
 * datasets too, but the agent already put those on the map under the composite
 * ids `handleAddDataset` mints, so painting the stream's raw-id copies would
 * duplicate every layer.
 */
export const useAgentAnalysisResult = () => {
  const store = useStore<RootState>();
  const dispatch = useDispatch();
  const { map } = useMap();
  const { handleAddSpatialAnalysisDatasetByType, handleRemoveDataset } =
    useDataset();

  /**
   * The result layers this hook painted. A re-run of the same workspace is a
   * new analysis with its own dataset ids, so the previous grid is taken off
   * rather than left stacked underneath the new one.
   */
  const paintedRef = useRef<
    { id: string; spatialType: SpatialAggregationType }[]
  >([]);

  const clearAnalysisResult = () => {
    for (const { id, spatialType } of paintedRef.current) {
      handleRemoveDataset(id, spatialType);
    }
    paintedRef.current = [];

    const datasets = store.getState().dataset.datasets;
    for (const ds of Object.values(datasets)) {
      if (ds.data.name === SPATIAL_ANALYSIS_RESULT_DATASET_NAME) {
        handleRemoveDataset(ds.data.id, ds.properties.spatialType);
      }
    }
  };

  const loadAnalysisResult = async (
    analysisId: string,
    signal?: AbortSignal
  ) => {
    const { datasets } = await streamAnalysisResult({
      analysisId,
      signal: signal ?? new AbortController().signal
    });

    // The panel unmounted, or a newer run superseded this one, while the stream
    // was draining. Painting now would leave a grid nothing accounts for.
    if (signal?.aborted) throw new Error("The draw was superseded.");

    const results = datasets.filter(
      ({ datasetData }) =>
        datasetData.name === SPATIAL_ANALYSIS_RESULT_DATASET_NAME
    );

    if (results.length === 0) {
      throw new Error(
        `The analysis ${analysisId} finished without a result to draw.`
      );
    }

    clearAnalysisResult();

    for (const { datasetData, dataset } of results) {
      const datasetId = datasetData.dataset_id;

      dispatch(addRawDataset({ dataset: datasetData, id: datasetId }));
      dispatch(addDataset({ dataset }));

      handleAddSpatialAnalysisDatasetByType({
        spatialType: datasetData.spatial_aggregation_type,
        newDataset: dataset,
        featureCollection:
          convertDatasetResponseToFeatureCollection(datasetData)
      });

      // Recorded before the check below, so the ledger always names what this
      // hook put in the store — a paint that only deferred still has to be
      // taken back off when the next run replaces it.
      paintedRef.current.push({
        id: datasetId,
        spatialType: datasetData.spatial_aggregation_type
      });

      // Every `handleAdd*Layer` returns nothing whether it painted, deferred on
      // an unparsed style, or no-op'd on a source that already exists — so the
      // call returning is not proof. The source being there is, and it also
      // makes the reader's "load again" idempotent: a repeat add no-ops and
      // this still passes.
      //
      // NOTE: source-present, not tiles-rendered. `waitForSource`
      // (src/utils/map.ts) would confirm the latter, but it resolves rather
      // than rejects on its timeout, which would turn a timeout into
      // `result_loaded: true`. Swap it in only with a rejecting timeout.
      if (!map?.getSource(datasetId)) {
        throw new Error(
          `The result layer for analysis ${analysisId} was not added to the map.`
        );
      }
    }
  };

  return { loadAnalysisResult, clearAnalysisResult };
};
